// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Icon, Styled, PreventPropagation} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useModal, usePopover, useDrawer} from '@supermove/hooks';
import {PaymentModel} from '@supermove/models';
import {colors} from '@supermove/styles';
import {List} from '@supermove/utils';

// App
import ActionMenuPopover from '@shared/design/components/ActionMenu/ActionMenuPopover';
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import DeletePaymentModal from '@shared/modules/Accounting/components/Payment/DeletePaymentModal';
import RefundPaymentDrawer from '@shared/modules/Accounting/components/Payment/RefundPaymentDrawer';
import RefundPayments from '@shared/modules/Accounting/components/Payment/RefundPayments';
import InvoiceStatus from '@shared/modules/Billing/enums/InvoiceStatus';
import PaymentMethodKind from '@shared/modules/PaymentMethod/enums/PaymentMethodKind';

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const getDeletePaymentTooltip = ({
  isInvoiceFinalized,
  isCreditCard,
}: {
  isInvoiceFinalized: boolean;
  isCreditCard: boolean;
}) => {
  if (isInvoiceFinalized) {
    return 'Cannot delete payments once invoice is finalized.';
  }
  if (isCreditCard) {
    return 'Cannot delete credit card payments.';
  }
  return null;
};

const PaymentActions = ({
  payment,
  isEnabledRefundPayment,
  refetch,
}: {
  payment: PaymentModel;
  isEnabledRefundPayment: boolean;
  refetch: () => void;
}) => {
  const paymentActionsPopover = usePopover();
  const deletePaymentModal = useModal({name: 'Delete Payment Modal'});
  const refundPaymentDrawer = useDrawer({name: 'Refund Payment Drawer'});
  const isInvoiceFinalized = _.get(payment, 'invoice.status') === InvoiceStatus.FINALIZED;
  const isCreditCard = PaymentMethodKind.getIsCreditCardKind(payment.method);

  return (
    <Row style={{flex: 1, justifyContent: 'center'}}>
      <PreventPropagation>
        <ActionMenuPopover
          popover={paymentActionsPopover}
          placement={ActionMenuPopover.Position.LeftStart}
          width={200}
          actions={[
            ...List.insertIf(isEnabledRefundPayment, {
              text:
                payment.method === PaymentMethodKind.PAYENGINE_CREDIT_CARD
                  ? 'Issue credit card refund'
                  : 'Record refund',
              onPress: () => {
                paymentActionsPopover.handleClose();
                refundPaymentDrawer.handleOpen();
              },
            }),
            {
              text: 'Delete payment',
              isDisabled: isInvoiceFinalized || isCreditCard,
              tooltip: getDeletePaymentTooltip({isInvoiceFinalized, isCreditCard}),
              onPress: () => {
                paymentActionsPopover.handleClose();
                deletePaymentModal.handleOpen();
              },
              color: colors.red.warning,
            },
          ]}
        >
          <TertiaryButton
            onPress={paymentActionsPopover.handleToggle}
            style={{paddingLeft: 8, paddingRight: 8}}
          >
            <Icon source={Icon.EllipsisV} size={16} color={colors.gray.secondary} />
          </TertiaryButton>
        </ActionMenuPopover>
      </PreventPropagation>
      <DeletePaymentModal
        isOpen={deletePaymentModal.isOpen}
        handleClose={deletePaymentModal.handleClose}
        payment={payment}
        refetch={refetch}
      />
      <RefundPaymentDrawer
        isOpen={refundPaymentDrawer.isOpen}
        handleClose={refundPaymentDrawer.handleClose}
        payment={payment}
        refetch={refetch}
      />
    </Row>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
PaymentActions.fragment = gql`
  ${DeletePaymentModal.fragment}
  ${RefundPaymentDrawer.fragment}
  ${RefundPayments.fragment}

  fragment PaymentActions on Payment {
    id
    method
    invoice {
      id
      status
    }
    paymentMethod {
      id
    }
    ...RefundPayments
    ...RefundPaymentDrawer
    ...DeletePaymentModal
  }
`;

export default PaymentActions;
